// mantine
import { useMemo } from 'react';
import { Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DateTime } from 'luxon';
// components
import { ResponsiveButton } from '@newo/ui-kit';
import { Upload } from 'lucide-react';
import type { TCustomerSessionReportParams } from '@newo/api/gateway';
import { DateRangeKey } from '@newo/ui-kit';
import ExportSessionsForm from './components/ExportSessionForm';
import Drawer from '@/components/Drawer';
// icons
// utils

import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';
import { TSessionFilters } from '@/pages/Sessions/Sessions.types';
import { useAccountQuery } from '@/services/customer/account/account.queries.ts';
import { useSendSessionReportToEmailMutation } from '@/services/customer/customer/customer.queries.ts';
import { getDateRange } from '@/modules/DateTime/utils';

interface ExportSessionsButtonProps {
    filters: TSessionFilters;
    total: number;
    dateRange?: DateRangeKey;
}

const ExportSessionsButton = ({
    filters,
    total,
    dateRange
}: ExportSessionsButtonProps) => {
    const [isOpened, handler] = useDisclosure(false);
    const { data: profile } = useProfileQuery();
    const { data: account } = useAccountQuery();
    const { mutateAsync: sendSessionReportToEmail } =
        useSendSessionReportToEmailMutation();

    const handleOpen = () => {
        handler.open();
    };

    const handleClose = () => {
        handler.close();
    };

    const getDataTimeRange = useMemo(() => {
        const parseFormat = 'yyyy-MM-dd hh:mm:ss a';

        if (dateRange) {
            const rangeValue = getDateRange(dateRange);
            return {
                from: rangeValue.from_datetime,
                to: rangeValue.to_datetime
            };
        }

        const from = DateTime.fromFormat(
            filters.from_datetime ?? '',
            parseFormat,
            { zone: 'local' }
        ).toISO({ suppressMilliseconds: true });

        const to = DateTime.fromFormat(filters.to_datetime ?? '', parseFormat, {
            zone: 'local'
        }).toISO({ suppressMilliseconds: true });

        return { from, to };
    }, [filters.from_datetime, filters.to_datetime, dateRange]);

    const handleSubmit = async (email: string) => {
        const { from, to } = getDataTimeRange;

        const data: TCustomerSessionReportParams = {
            email,
            from_datetime: from ?? undefined,
            to_datetime: to ?? undefined,
            tenant: profile?.tenant
        };

        return sendSessionReportToEmail({
            data
        });
    };

    return (
        <>
            <Drawer
                name="export-sessions"
                opened={isOpened}
                position="right"
                size="xs"
                title="Export sessions"
                onClose={handleClose}
            >
                <div className="mb-6 text-gray-6">
                    For the selected period you have{' '}
                    <span className="font-bold text-black">{total}</span>{' '}
                    sessions.
                </div>
                <div className="mb-6">
                    {filters.from_datetime ? undefined : (
                        <Alert color="yellow" title="Warning">
                            Please specify a date range in filters to export
                            sessions otherwise the process may take a long time.
                        </Alert>
                    )}
                </div>
                <ExportSessionsForm
                    email={account?.email ?? ''}
                    onSubmit={handleSubmit}
                />
            </Drawer>
            <ResponsiveButton
                icon={<Upload />}
                isDisabled={total === 0}
                onClick={handleOpen}
            >
                Export .csv
            </ResponsiveButton>
        </>
    );
};

export default ExportSessionsButton;
